/** Tracker cheat-sheet HTML (ported from helpModal.ts). */
export const CHEAT_SHEET_HTML = `
<section>
  <h3>Cell layout</h3>
  <p>Each instrument column is <code>Note</code> (4) · <code>Vol</code> (2) · <code>FX</code> (9). Type overwrites the glyph under the cursor and advances.</p>
  <pre>N   V  F
C-4 FF C04V40</pre>
  <p>DSL form uses spaces and <code>|</code> between tracks:</p>
  <pre>00 C-4 FF C04 | .... .. .. | …</pre>
</section>

<section>
  <h3>Notes &amp; volume</h3>
  <ul>
    <li><strong>Notes</strong> — <code>C-2</code>, <code>C#3</code>, <code>Db4</code> (A–G, optional <code>#</code>/<code>b</code>, octave).</li>
    <li><strong>Empty</strong> — <code>....</code> (dots). No new note that row. In the note field, <kbd>,</kbd> pastes the last edited note.</li>
    <li><strong>Note-off</strong> — <code>---</code>, <code>===</code>, or <code>OFF</code> closes the gate.</li>
    <li><strong>Vol</strong> — hex <code>00</code>–<code>FF</code> (velocity). <code>..</code> = omit → full level. On an empty-note row, Vol patches the sounding voice’s level (no retrigger).</li>
  </ul>
</section>

<section>
  <h3>Effects (FX column)</h3>
  <p>Letter + 1–2 hex digits (<code>?xx</code> chance is decimal). The grid always stores <strong>2</strong> value digits. Glue several with no spaces (fits <strong>4</strong> in the column). Kernel FX (<code>V</code>/<code>U</code>/<code>D</code>/<code>P</code>/<code>A</code>/<code>F</code>/<code>B</code>) work on synth, drums, and SoundFont — pitch via rate / tone; <code>F</code> is a simple LPF on drums/SF2.</p>
  <table>
    <thead><tr><th>Code</th><th>Meaning</th><th>Value</th></tr></thead>
    <tbody>
      <tr><td><code>Vxy</code></td><td>Vibrato</td><td><code>x</code> = speed (×1.25&nbsp;Hz), <code>y</code> = depth (×0.35&nbsp;st)</td></tr>
      <tr><td><code>Uxx</code></td><td>Slide up</td><td>rate (semitones/sec)</td></tr>
      <tr><td><code>Dxx</code></td><td>Slide down</td><td>rate (semitones/sec)</td></tr>
      <tr><td><code>Pxx</code></td><td>Portamento</td><td>glide to this note (no env retrigger)</td></tr>
      <tr><td><code>Cxx</code></td><td>Cut / gate</td><td>length ≈ <code>xx/16</code> steps (host-only)</td></tr>
      <tr><td><code>C--</code></td><td>Open hold</td><td>hold until next note / <code>---</code> / envelope end</td></tr>
      <tr><td><code>Axy</code></td><td>Arpeggio</td><td>cycle base → +<code>x</code> st → +<code>y</code> st; <code>0</code> skips that stage (e.g. <code>A37</code> minor triad, <code>A50</code> fifth only). ~one full cycle per step</td></tr>
      <tr><td><code>Fxx</code></td><td>Filter cutoff</td><td><code>80</code>≈0 oct, <code>00</code>≈−2, <code>FF</code>≈+2</td></tr>
      <tr><td><code>Bxx</code></td><td>Note pan</td><td>voice pan for this hit: <code>00</code>=full L, <code>80</code>=center, <code>FF</code>=full R. Omit → slot / strip pan</td></tr>
      <tr><td><code>Rxy</code></td><td>Retrigger / ratchet</td><td><code>y</code> = hits (1–15), <code>x</code> = volume decay. Pair with <code>Cxx</code> so the ratchet spans the gate you want (e.g. <code>C50R04</code>) — plain <code>R</code> alone packs all hits into one step</td></tr>
      <tr><td><code>Gxy</code></td><td>Gate / stutter</td><td><code>y</code> = slices (1–15), <code>x</code> = open duty (<code>0</code>→50%, else <code>x/16</code>). One held note, level chopped — envelopes/FX keep running (unlike <code>R</code>). Pair with <code>C</code> (e.g. <code>C50G04</code>)</td></tr>
      <tr><td><code>Exx</code></td><td>Note delay</td><td>onset at <code>xx/256</code> of the step (host-only)</td></tr>
      <tr><td><code>?xx</code></td><td>Chance</td><td>decimal <code>00</code>–<code>99</code> = % chance this note plays (<code>?00</code> never, omit FX = always). Host-only</td></tr>
    </tbody>
  </table>
  <p class="examples"><strong>Examples:</strong> <code>C04</code> short hit · <code>V68</code> vibrato · <code>A37</code> minor triad arp · <code>B00</code>/<code>BFF</code> hard L/R · <code>?50</code> coin-flip · <code>C50R04</code> 4-hit ratchet · <code>C50G04</code> 4-slice gate · <code>C04?75</code> cut + 75% chance</p>
</section>

<section>
  <h3>Do effects stick?</h3>
  <ul>
    <li><strong>New pitched note</strong> — FX reset. Re-type any effect you still want (vibrato, slide, filter, arpeggio, …).</li>
    <li><strong>Empty-note row</strong> — FX patches the sounding voice; Vol alone changes level mid-hold.</li>
    <li><strong>Cut / Retrigger / Gate / Delay / Chance (<code>C</code>/<code>R</code>/<code>G</code>/<code>E</code>/<code>?</code>)</strong> — host scheduling for that hit only; do not carry.</li>
    <li>Default gate ≈ one step; with porta and no cut ≈ 1.5 steps. <code>C10</code> ≈ 1 step, <code>C80</code> ≈ half bar @ 16ths, max 16 steps.</li>
    <li><code>R</code> should be used with <code>C</code>: the cut sets the window the ratchet fills. <code>C50R04</code> = 4 hard-retriggers (fresh attacks). Plain <code>R04</code> alone squeezes all hits into one step.</li>
    <li><code>G</code> is stutter/slice: same window idea as <code>R</code>, but one continuous note with level gating — filter/vibrato/arp keep going. <code>C50G04</code> = 4 chops at 50% duty; <code>G84</code> = 4 slices, duty <code>8/16</code>; <code>GC4</code> = duty <code>12/16</code> (more open).</li>
    <li><code>?xx</code> is decimal percent (not hex). Rolled once per cell; if it fails, the whole hit (including <code>R</code>/<code>G</code>) is skipped.</li>
    <li><code>E80</code> ≈ half-step late onset; combine with <code>R</code>/<code>G</code> for delayed chops.</li>
  </ul>
</section>

<section>
  <h3>Pattern DSL directives</h3>
  <pre>@bpm 128
@grid 16
@steps 64
@loop on
I1 Melody
P0
00 C-4 FF C08 | …</pre>
  <p><code>@grid</code> is steps per whole note (toolbar <strong>Grid</strong>): <code>16</code> = 16ths. <code>I1</code>–<code>I8</code> name the eight instrument slots. Unknown <code>@</code> lines become warnings.</p>
</section>

<section class="keys-section">
  <h3>Keys</h3>

  <div class="keys-group">
    <h4>Transport &amp; panels</h4>
    <ul class="keys-list">
      <li><span class="keys-combo"><kbd>Space</kbd></span><span class="keys-desc">Play from cursor / stop</span></li>
      <li><span class="keys-combo"><kbd>Shift</kbd>+<kbd>Enter</kbd></span><span class="keys-desc">Jump to row 0 (restarts playback if playing)</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>I</kbd></span><span class="keys-desc">Toggle instruments panel</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>F</kbd></span><span class="keys-desc">Toggle FX panel</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>A</kbd></span><span class="keys-desc">Toggle arrange panel</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>L</kbd></span><span class="keys-desc">Toggle samples panel</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>P</kbd></span><span class="keys-desc">Toggle performance</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>M</kbd></span><span class="keys-desc">Toggle mixer</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+<kbd>I</kbd></span><span class="keys-desc">Toggle Author for current track (tracker cursor, else instruments panel)</span></li>
    </ul>
  </div>

  <div class="keys-group">
    <h4>Navigation</h4>
    <ul class="keys-list">
      <li><span class="keys-combo">← →</span><span class="keys-desc">One glyph</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+← →</span><span class="keys-desc">Prev / next instrument</span></li>
      <li><span class="keys-combo">↑ ↓</span><span class="keys-desc">Rows</span></li>
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd>+↑ ↓</span><span class="keys-desc">Jump a bar</span></li>
      <li><span class="keys-combo"><kbd>Tab</kbd></span><span class="keys-desc">Next track</span></li>
      <li><span class="keys-combo"><kbd>Shift</kbd>+arrows</span><span class="keys-desc">Extend selection · drag also selects</span></li>
      <li><span class="keys-combo"><kbd>Esc</kbd></span><span class="keys-desc">Clear selection</span></li>
      <li><span class="keys-combo"><kbd>Shift</kbd><kbd>Shift</kbd></span><span class="keys-desc">Go to row / marker (or toolbar search)</span></li>
    </ul>
  </div>

  <div class="keys-group">
    <h4>Edit</h4>
    <ul class="keys-list">
      <li><span class="keys-combo"><kbd>⌘/Ctrl</kbd> <kbd>Z</kbd> <kbd>Y</kbd> <kbd>C</kbd> <kbd>X</kbd> <kbd>V</kbd></span><span class="keys-desc">Undo / redo (global) / copy / cut / paste — text fields keep native undo</span></li>
      <li><span class="keys-combo"><kbd>S</kbd> / <kbd>M</kbd></span><span class="keys-desc">Solo / mute (header or instrument strip; solo overrides mute)</span></li>
      <li><span class="keys-combo">Type</span><span class="keys-desc">Notes &amp; hex in place; note letter auto-fills <code>-octave</code> (last octave, default 4)</span></li>
      <li><span class="keys-combo"><kbd>,</kbd></span><span class="keys-desc">In the note field — repeat last edited note, then advance a row</span></li>
      <li><span class="keys-combo"><kbd>.</kbd></span><span class="keys-desc">Clear a glyph</span></li>
      <li><span class="keys-combo"><kbd>Backspace</kbd> / <kbd>Delete</kbd></span><span class="keys-desc">Clear</span></li>
    </ul>
  </div>

  <div class="keys-group">
    <h4>Arm mode</h4>
    <p class="keys-lead"><kbd>⌘/Ctrl</kbd>+<kbd>K</kbd> or the tracker tools rail — orange focus ring; piano keys write full notes and advance a row. <kbd>Esc</kbd> exits arm mode.</p>
    <ul class="keys-list">
      <li><span class="keys-combo">Arrows</span><span class="keys-desc">Move by cell</span></li>
      <li><span class="keys-combo"><kbd>Z</kbd> / <kbd>X</kbd></span><span class="keys-desc">Octave down / up</span></li>
      <li><span class="keys-combo"><kbd>-</kbd></span><span class="keys-desc">Toggle note-off (<code>---</code>)</span></li>
      <li><span class="keys-combo">Drums</span><span class="keys-desc">Notes select pads (Studio Kit: chromatic from C4; GM Drums: MIDI 36–51)</span></li>
      <li><span class="keys-combo">SoundFont</span><span class="keys-desc">Notes play pitched / mapped samples (incl. Tama RockSTAR from MIDI import)</span></li>
    </ul>
  </div>

  <div class="keys-group">
    <h4>Mouse</h4>
    <ul class="keys-list">
      <li><span class="keys-combo">Row number</span><span class="keys-desc">Click to add / edit a label + tint</span></li>
      <li><span class="keys-combo">Track header</span><span class="keys-desc">Double-click title to rename · right-click for color</span></li>
    </ul>
  </div>
</section>

<section>
  <h3>Synth vs drums vs SoundFont vs tracker FX</h3>
  <p>Left dock follows the <strong>tracker cursor</strong> (no channel tabs). The strip edits the track <strong>name</strong>, <strong>volume</strong>, and <strong>pan</strong>; kind switches <strong>Synth</strong> / <strong>Drums</strong> / <strong>SoundFont</strong>. Synth / Master FX panels set the <em>instrument</em> and the <em>master bus</em>. Tracker FX (<code>V U D P C A F</code>) are per-row note commands on top of that — they are not the same as chorus/reverb in Master FX.</p>
</section>
`;
